import { useI18nContext } from "@root/utils/organisation";
import { type MouseEventHandler, useCallback, useEffect, useMemo, useState } from "react";
import {
  DateRangePickerViews,
  type IDateRange,
  type IDateRangePickerChip,
  type IDateRangePickerInnerChip,
  type OnChangeDateRange,
  type TDateRangePickerViews,
} from "../types";
import { constructChips } from "./chips";
import { verifyRequired } from "./date";
import { formatRange } from "./input";
import { getDefaultView } from "./views";

export const useDRP = <Chip extends string = string>(
  open: boolean,
  value: IDateRange,
  onChange: OnChangeDateRange<Chip>,
  defaultView: DateRangePickerViews = DateRangePickerViews.DAY,
  views?: TDateRangePickerViews,
  chips?: IDateRangePickerChip<Chip>[],
  chip?: Chip | null,
  externalAnchor?: Element | null,
  onClose?: OnChangeDateRange<Chip>,
  required?: { startDate: boolean; endDate: boolean },
  minDate?: Date,
  maxDate?: Date,
  endDateDisabled?: boolean
) => {
  const { timeZone } = useI18nContext();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);

  const [selectedChip, setSelectedChip] = useState<IDateRangePickerInnerChip<Chip> | null>(null);
  const [selectedView, setSelectedView] = useState<DateRangePickerViews>(getDefaultView(defaultView, value, views));
  const [error, setError] = useState<{ startDate: string; endDate: string }>({
    startDate: "",
    endDate: "",
  });

  const setRangeWithChange = useCallback(
    (newRange: IDateRange) => {
      setSelectedChip(null);
      setError({ startDate: "", endDate: "" });
      if (newRange.startDate && newRange.endDate && newRange.startDate.getTime() > newRange.endDate.getTime()) {
        setError({ startDate: "La date de début ne peut pas être supérieure à la date de fin", endDate: "" });
      }
      onChange(newRange, null);
    },
    [onChange]
  );

  const handleClose = () => {
    if (value.startDate && value.endDate && value.startDate.getTime() > value.endDate.getTime()) {
      setError({ startDate: "La date de début ne peut pas être supérieure à la date de fin", endDate: "" });
      return;
    }
    if (required) {
      const error = verifyRequired(value, required);
      setError(error);
    }
    if (onClose) {
      onClose(value, selectedChip?.value ?? null);
    }
  };

  const inputValue = useMemo(() => {
    if (selectedChip) {
      return selectedChip.label;
    }
    return formatRange(value, selectedView, timeZone);
  }, [value, selectedChip, selectedView, timeZone]);

  const handleClear: MouseEventHandler<HTMLButtonElement> = (evt) => {
    evt.stopPropagation();
    setRangeWithChange({ startDate: null, endDate: null });
  };

  const handleRangeChange = (range: IDateRange) => {
    setSelectedChip(null);
    onChange(range, null);
  };

  const handleChipChange = (chip: IDateRangePickerInnerChip<Chip> | null) => {
    setSelectedChip(chip);
    onChange(chip ? { startDate: chip.startDate, endDate: chip.endDate } : value, chip?.value ?? null);
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: Only at init
  const innerChips = useMemo(() => {
    if (chips) {
      const innerChips = constructChips(chips, selectedView, timeZone);
      if (chip) {
        handleChipChange(innerChips.find((c) => c.value === chip) ?? null);
      }
      return innerChips;
    }
    return null;
  }, [selectedView]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (chip && innerChips) {
      handleChipChange(innerChips.find((c) => c.value === chip) ?? null);
    } else {
      setSelectedChip(null);
    }
  }, [chip]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    if (required && (error.startDate || error.endDate)) {
      const error = verifyRequired(value, required);
      setError(error);
    }
  }, [required]);

  return {
    inputValue,
    innerChips,
    range: value,
    handleRangeChange,
    setRangeWithChange,
    open: typeof open === "undefined" ? open : open,
    anchorEl: typeof externalAnchor === "undefined" ? anchorEl : externalAnchor,
    setAnchorEl,
    selectedChip,
    setSelectedChip,
    selectedView,
    setSelectedView,
    handleClear,
    handleClose,
    views,
    handleChipChange,
    error,
    setError,
    required,
    minDate,
    maxDate,
    endDateDisabled,
    timeZone,
  };
};
